import React, { type ReactNode, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { FloorActivity, FloorActivityCardVariant, NftCard, NftCardSectionVariant } from '@pkg/components';
import { type IncomingProps } from '@pkg/components/FloorActivity/components';
import { type UtcMilliseconds } from '@pkg/types';
import {
    type FloorGameActivity,
    FloorGameCrypto,
    type FloorGameIncomingEvent,
    FloorGameTierLabel,
    type NftCommonLibrary,
    type NftMarket,
    type NftValueCommon,
    type YatOwnedNft,
} from '@pkg/types/app/floorGame';

import { determineDeathGame, formatUsdPrice } from '@app/helpers/floorGame';

export interface ActivityProps {
    id: YatOwnedNft['id'];
    value: NftValueCommon;
    tier?: FloorGameTierLabel;
    activity?: FloorGameActivity;
    incoming?: FloorGameIncomingEvent;
    buyUrl?: NftMarket['buyUrl'];
    lastSale?: UtcMilliseconds;
    nextNfts?: NftCommonLibrary;
    listingsCount?: number;
    estimation?: boolean;
    compact?: boolean;
    // TODO: Temporary way to render <TicksMeter />.
    children?: ReactNode;
}

// TODO: Replace this with real data.
const MOCK_INCOMNG_DATA: Pick<IncomingProps, 'yat' | 'twitter' | 'saleValue'> = {
    yat: '😊🫶🎄✨',
    twitter: 'chimbeefington',
    saleValue: {
        currency: FloorGameCrypto.Ethereum,
        price: 1,
        localConversion: formatUsdPrice(1234.51),
    },
};

export function Activity({
    id,
    value,
    tier = FloorGameTierLabel.Tier1,
    // activity = FloorGameActivity.Quiet,
    incoming,
    buyUrl,
    lastSale,
    nextNfts = [],
    listingsCount = 0,
    estimation = false,
    compact = false,
    children,
}: ActivityProps) {
    // We could end up in a situation where `lastSale` changes while
    // `hideDeathGame` is `true`, and the toggle events do not get
    // called in order to reset state back to `false`.
    const [hideDeathGame, setHideDeathGame] = useState(false);

    function handleShowDeath() {
        setHideDeathGame(false);
    }

    function handleHideDeath() {
        setHideDeathGame(true);
    }

    // const tickStatus = activityToTickStatus(activity);

    // TODO: Should this be one value in a `status / event` prop?
    const hasDied = determineDeathGame(lastSale);

    const incomingMarkup = incoming ? (
        <FloorActivity.Incoming activity={incoming} compact={compact} {...MOCK_INCOMNG_DATA} />
    ) : null;

    const estValueMarkup =
        estimation && !incoming ? (
            <FloorActivity.Card variant={FloorActivityCardVariant.Light}>
                <FloorActivity.EstValue {...value} />
            </FloorActivity.Card>
        ) : null;

    const priceGameMarkup =
        (hasDied && !hideDeathGame) || estimation ? null : (
            <FloorActivity.PriceGame
                key={`NftCard-PriceGame-${id}`}
                {...value}
                tier={tier}
                reviveAction={hasDied ? buyUrl : undefined}
                nextNfts={nextNfts}
                listingsCount={listingsCount}
                compact={compact}
            >
                {/* Ticks should be rendered here. */}
                {children}
            </FloorActivity.PriceGame>
        );

    const deathGameMarkup =
        hasDied && !hideDeathGame && !estimation ? (
            <FloorActivity.DeathGame
                key={`NftCard-DeathGame-${id}`}
                timestamp={lastSale}
                compact={compact}
                // We can't utilize `onBlur`, because the component
                // becomes unmounted once we have focused.
                // onBlur={handleShowDeath}
                onFocus={handleHideDeath}
            />
        ) : null;

    /*
    const resurrectionMarkup =
        hasDied && !hideDeathGame && !estimation ? (
            <FloorActivity.Resurrection
                key={`NftCard-Resurrection-${id}`}
                label={mockResurrectionLabel}
                compact={compact}
            />
        ) : null;
    */

    /*
    const memeMarkup =
        hasDied && !hideDeathGame && !estimation ? (
            <FloorActivity.Meme
                key={`NftCard-Meme-${id}`}
                // variant={FloorActivityMemeId.Chloe}
                compact={compact}
            />
        ) : null;
        */

    const floorPriceMarkup =
        estimation || incoming ? null : (
            <FloorActivity.Card
                spaceBefore={!compact}
                onMouseEnter={hasDied ? handleHideDeath : undefined}
                onMouseLeave={hasDied ? handleShowDeath : undefined}
            >
                <AnimatePresence initial={false} mode="wait">
                    {priceGameMarkup}
                    {deathGameMarkup}
                </AnimatePresence>
            </FloorActivity.Card>
        );

    // TODO: Probably need another <AnimatePresence />
    // within <FloorActivity />.
    return (
        <NftCard.Section variant={NftCardSectionVariant.Activity}>
            <FloorActivity>
                {incomingMarkup}
                {estValueMarkup}
                {floorPriceMarkup}
            </FloorActivity>
        </NftCard.Section>
    );
}
